import { Box } from "@mui/material"
import { useFocusable } from "@noriginmedia/norigin-spatial-navigation"
import React, { useCallback, useEffect, useState } from "react"
import { isFavoritedContent, playerStore } from "src/store/PlayerStore"

import useSdk, { ErrorLevel } from "../../../../hooks/use-sdk"
import useTranslate from "../../../../hooks/use-translate"
import { appStore } from "../../../../store/AppStore"
import { s } from "../../../../utilities/common"
import { theme } from "../../../../utilities/theme"

export const FavoriteButton = ({ focusKey }: { focusKey?: string }) => {
  const t = useTranslate()
  const myStage = appStore((state) => state.myStage)
  const content = playerStore((state) => state.content)
  const trackId = playerStore((state) => state.trackId)
  const setMyStage = appStore((state) => state.setMyStage)
  const [marked, setMarked] = useState<boolean>()
  const sdk = useSdk(ErrorLevel.NoError)

  useEffect(() => {
    setMarked(isFavoritedContent(myStage, content, trackId))
  }, [myStage, content, trackId])

  const onPress = useCallback(async () => {
    if (!content) return
    const contentId =
      content.__typename === "VodConcert" && trackId ? trackId : content.id
    if (!contentId) return
    setMarked(!marked)
    if (!marked) sdk.addFavorite({ input: { contentId } })
    if (marked) sdk.removeFavoriteByContentId({ input: { contentId } })
    const result = await sdk.myStagePage({
      sliderPageSize: 7,
      cursor: undefined,
    })
    setMyStage(result)
  }, [content, trackId, marked])

  const { focused, ref, focusSelf } = useFocusable({
    focusKey: focusKey,
    onEnterPress: onPress,
  })
  return (
    <Box
      sx={{ height: s("72px"), width: s("72px") }}
      tabIndex={0}
      aria-label={t("contextmenu__add_favorite")} // "contextmenu__remove_favorite"
      onMouseEnter={() => focusSelf({ noScroll: true })}
      onMouseDown={onPress}
      ref={ref}
    >
      <svg
        width={s("72")}
        height={s("72")}
        viewBox="0 0 72 72"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle
          cx="36"
          cy="36"
          r="36"
          fill={focused ? "white" : theme.palette.new.playerButton}
        />
        {marked ? (
          <path
            d="M28.043 48.1055C27.3633 47.5898 27.2227 46.7344 27.5859 45.6914L29.7656 39.2344L24.2109 35.2617C23.2969 34.6172 22.8867 33.8438 23.1797 33.0234C23.4492 32.2148 24.2227 31.8281 25.3242 31.8398L32.1328 31.8867L34.1953 25.3828C34.5469 24.3164 35.1445 23.707 36 23.707C36.8438 23.707 37.4414 24.3164 37.793 25.3828L39.8555 31.8867L46.6523 31.8398C47.7656 31.8281 48.5391 32.2148 48.8086 33.0234C49.0898 33.8438 48.6914 34.6172 47.7773 35.2617L42.2227 39.2344L44.4023 45.6914C44.7656 46.7344 44.625 47.5898 43.9453 48.1055C43.2539 48.6328 42.4102 48.4805 41.4961 47.8125L36 43.7695L30.4922 47.8125C29.5781 48.4805 28.7344 48.6328 28.043 48.1055Z"
            fill={focused ? theme.palette.new.playerButton : "white"}
          />
        ) : (
          <path
            d="M28.043 48.1055C27.3633 47.5898 27.2227 46.7344 27.5859 45.6914L29.7656 39.2344L24.2109 35.2617C23.2969 34.6172 22.8867 33.8438 23.1797 33.0234C23.4492 32.2148 24.2227 31.8281 25.3242 31.8398L32.1328 31.8867L34.1953 25.3828C34.5469 24.3164 35.1445 23.707 36 23.707C36.8438 23.707 37.4414 24.3164 37.793 25.3828L39.8555 31.8867L46.6523 31.8398C47.7656 31.8281 48.5391 32.2148 48.8086 33.0234C49.0898 33.8438 48.6914 34.6172 47.7773 35.2617L42.2227 39.2344L44.4023 45.6914C44.7656 46.7344 44.625 47.5898 43.9453 48.1055C43.2539 48.6328 42.4102 48.4805 41.4961 47.8125L36 43.7695L30.4922 47.8125C29.5781 48.4805 28.7344 48.6328 28.043 48.1055ZM30.9141 44.168C30.9258 44.1914 30.9492 44.168 30.9844 44.1445L35.1094 40.8633C35.8125 40.3242 36.1758 40.3242 36.8789 40.8633L41.0039 44.1445C41.0391 44.168 41.0625 44.1914 41.0742 44.168C41.0859 44.1445 41.0977 44.1328 41.0859 44.0859L39.2344 39.1641C38.9531 38.332 38.9766 38.0156 39.7617 37.5L44.1445 34.582C44.1914 34.5469 44.2031 34.5352 44.1914 34.5117C44.1914 34.4883 44.168 34.4883 44.1094 34.4883L38.8711 34.6992C37.9805 34.7227 37.6641 34.5469 37.4414 33.7031L36.0469 28.6406C36.0352 28.5938 36.0117 28.5703 36 28.5703C35.9766 28.5703 35.9531 28.5938 35.9414 28.6406L34.5469 33.7031C34.3242 34.5469 34.0078 34.7227 33.1289 34.6992L27.8789 34.4883C27.832 34.4883 27.8086 34.4883 27.7969 34.5117C27.7969 34.5352 27.8086 34.5586 27.8438 34.582L32.2266 37.4883C33.0234 38.0156 33.0469 38.332 32.7539 39.1641L30.9023 44.0859C30.8906 44.1328 30.9023 44.1445 30.9141 44.168Z"
            fill={focused ? theme.palette.new.playerButton : "white"}
          />
        )}
      </svg>
    </Box>
  )
}
